import * as qs from 'qs';
import 'ix/add/asynciterable-operators/zip';
import { AsyncIterableX } from 'ix/asynciterable';
import { fromEventSource } from './fromEventSource';
import { Table, RecordBatchReader } from 'apache-arrow';
import { bakedOpen, bakedPipe, fetcher, resolveBakedName } from './stubs';
import { createLogger } from '@graphistry/common/logger';
import { traceFields } from '@graphistry/common/traceparent';

const logger = createLogger(__filename);

export const _API = `${ process.env.STUB_DEPENDENCIES === 'true' || process.env.STUB_DEPENDENCIES === true ? (process.env.BASE_PATH || 'http://localhost:3000'):''}/streamgl`; // stub dev: build-time server origin
//export const _API = '/streamgl';
export const open =    loadTables(`open`);
export const load =    loadTables(`read`);
export const play = streamBatches(`play`);
export const stop = streamBatches(`stop`);
export const tick = streamBatches(`tick`);
export const read = streamBatches(`read`);

/**
 * Apply initial encodings after /open completes.
 * Called by withArrowData.js after the GPU session is ready.
 * Uses Falcor call route via WebSocket to ensure same worker handles request.
 * After encodings are applied, refetches tables from GPU to get encoded colors.
 * Returns a Promise with updated props including fresh node/edge tables.
 */
export const applyEncodings = (trail) => async function(props = {}) {
    const sessionId = props.session;
    const $falcor = props.$falcor;
    // encodingsApplied comes from Arrow metadata set by streamgl-gpu
    // It indicates whether the GPU already has encodings applied (from a previous window)
    const encodingsApplied = props.encodingsApplied || false;

    console.info('applyEncodings: starting', { sessionId, encodingsApplied, ...traceFields(trail) });

    if (!$falcor) {
        throw new Error(`applyEncodings: no Falcor model available, sessionId=${sessionId}, traceparent=${trail}`);
    }

    // Use Falcor call route via WebSocket
    // This ensures the request goes to the same worker that has the session state
    // Clone the Falcor model to get the view-level path (first 4 elements)
    // $falcor._path is typically ['workbooksById', id, 'viewsById', id, 'scene']
    // We need the view level path for the applyEncodings route
    const viewPath = $falcor._path ? $falcor._path.slice(0, 4) : [];
    const viewModel = $falcor._clone({ _path: viewPath });
    // Pass trail (traceparent) and encodingsApplied flag as arguments
    // encodingsApplied tells the server if GPU already has encodings (skip GPU POST)
    await viewModel.call('applyEncodings', [trail, encodingsApplied]).toPromise();
    console.info('applyEncodings: complex encodings applied on server', { sessionId, encodingsApplied, ...traceFields(trail) });

    // NOTE: We do NOT call load() here to refetch tables.
    // The GPU worker sets encodingsUpdated=true and sends an SSE notification.
    // The SSE stream (dynamicGraphUpdates) receives this notification and triggers
    // selectGraphAPIUpdateCommand which calls graphAPI.load() to fetch encoded data.
    // This is the correct async flow - applyEncodings just triggers the server-side
    // encoding, and the SSE mechanism handles data refresh to the renderer.

    return props;  // Return /open data - SSE will trigger /read for encoded data
};

// In STUB_DEPENDENCIES mode there are no GPU workers, so every /streamgl data fetch
// must be stubbed regardless of id: the baked open buffers embed their own session id,
// which replaces the magic url id (abc/cycle) after /open, so match by route + remember
// which baked dataset was chosen rather than by exact uri.
const STUBBED = process.env.STUB_DEPENDENCIES === 'true' || process.env.STUB_DEPENDENCIES === true;
let stubDataset = 'lesMis'; // flips when a magic id (cycle/twitter/... see stubs.js bakedRegistry) is first seen

export function graphUpdatesObservable(trail) { return (props = {}) => {
    // EventSource (used by fromEventSource for /pipe) cannot send custom headers, so the trail
    // (traceparent) below is ignored on the wire. Forward it as a query param too so the router can
    // nest a transparent-reopen under graph.viz.load. qs.stringify drops it when trail is undefined.
    const uri = `${
        [_API, 'pipe'].join('/')}?${
        qs.stringify({ id: props.session, traceparent: trail })}`;
    if (STUBBED) {
        const baked = resolveBakedName(props.session);
        if (baked) { stubDataset = baked; }
        console.debug('stubbing graphUpdatesObservable', {stubDataset, ...traceFields(trail)});
        return bakedPipe();
    }
    return fromEventSource(uri, { withCredentials: false, headers: {trail} });
}}

function loadTables(route) { return (trail) => {
    const uri = [_API, route].filter(Boolean).join('/');
    return async function(props = {}) {
        //console.debug('@loadTables', {route, props});
        let result = {};
        try {
            const id = props.session, v = +props.graph.version || 0;
            const body = { id, v, layoutOptions: props.graph.layoutOptions };
            const recordBatchReaders = fetchRecordBatches(`${uri}?id=${encodeURIComponent(id)}`, body, trail);
            const tables = AsyncIterableX.from(['node', 'edge']).zip(recordBatchReaders);
            
            try {
                for await (const pair of tables) {
                    // destructuring this breaks babel somehow
                    const name = pair[0];
                    const reader = pair[1];
                    result = aggregateTableResults(result, name, await Table.from(reader));
                }
                props.graph.version = result.version;
            } catch (e) {
                console.warn('loadTables inner exn', {e, ...traceFields(trail)});
            }
        } catch (e) { console.warn('loadTables outer exn', {e, ...traceFields(trail)}); }
        //console.debug('////loadTables');
        return { ...props, ...result };
    }
}}

function streamBatches(route) { return (trail) => {
    const uri = [_API, route].filter(Boolean).join('/');
    return async function* (props = {}) {
        const viewport = getViewport(props);
        const id = props.session, v = +props.graph.version || 0;
        const body = { id, v, layoutOptions: props.graph.layoutOptions, viewport };
        const tables = [];
        try {
            let nodeVersionsMatchGraph = true;
            const recordBatchReaders = fetchRecordBatches(`${uri}?id=${encodeURIComponent(id)}`, body, trail);
            for await (const reader of recordBatchReaders) {
                if (reader instanceof Error) {
                    throw reader;
                  }

                const batches = [];
                for await (const recordBatchInfo of eachRecordBatchWithTimingInfo(reader)) {
                    // If the versions don't match, the server will send us a full set of two new tables.
                    // aggregate the results into a pair of tables, and yield the same shaped result object
                    // as the `loadTables()` function does above.
                    // Otherwise if the versions match, we can yield each batch one at a time since we know
                    // the final length is the same as the current length in the renderer.
                    if (!nodeVersionsMatchGraph || recordBatchInfo.version !== v) {
                        nodeVersionsMatchGraph = false;
                        batches.push(recordBatchInfo.nodesUpdate.batch);
                    } else {
                        yield { ...props, ...recordBatchInfo, session: id };
                    }
                }
                // If the versions are the same, the server's only going to send us one table
                if (nodeVersionsMatchGraph) {
                    return;
                }
                tables.push(new Table(batches));
            }

            const result = ['node', 'edge'].reduce((result, name, i) => {
                try {
                    if (tables[i]) {
                        return aggregateTableResults(result, name, tables[i]);
                    }
                } catch (e) {}
                return result;
            }, {})
    
            if (tables.filter(Boolean).length === 2) {
                props.graph.version = result.version;
            }
    
            yield { ...props, ...result };
        } catch (e) {
            logger.error(e, 'Error while reading the graph data stream! traceparent=%s', trail);
            // re-throw so the chain of observables can catch the error (see withArrowData)
            throw e;
        }
    }
}}

export function aggregateTableResults(result = {}, name, table) {
    result[`${name}s`] = table;
    result[`${name}sTableLength`] = table.length;
    result[`${name}sUpdate`] = { table, offset: 0 };
    result.version = +table.schema.metadata.get('version');
    result.session = table.schema.metadata.get('session');

    // Extract encodingsApplied flag from Arrow metadata.
    // Set by GPU worker for already-opened sessions to signal frontend
    // to skip redundant applyEncodings calls.
    const encodingsApplied = table.schema.metadata.get('encodingsApplied');
    if (encodingsApplied === 'true') {
        result.encodingsApplied = true;
    }

    let bbox = table.schema.metadata.get('globalBoundBox');
    if (bbox && !result.globalBoundBox) {
        try {
            bbox = JSON.parse(bbox);
            result.globalBoundBox = bbox;
        } catch (e) {console.error('aggregateTableResults exn', e); }
    }
    //console.debug('////aggregateTableResults', {result, name, table});
    return result;
}

async function* eachRecordBatchWithTimingInfo(reader) {
    let numBatches = 0;
    let tableLength = 0;
    let batchOffset = 0;
    let globalBoundBox = undefined;
    let requestTime, averageTime = 0;
    let endTime, startTime = Date.now();
    for await (const recordBatch of reader) {
        requestTime = (endTime = Date.now()) - startTime;
        tableLength = tableLength || +recordBatch.schema.metadata.get('length');
        averageTime = ++numBatches === 1 ? requestTime : (averageTime + requestTime) / 2;
        if (!globalBoundBox && (globalBoundBox = recordBatch.schema.metadata.get('globalBoundBox'))) {
            try { globalBoundBox = JSON.parse(globalBoundBox); } catch (e) { globalBoundBox = undefined; }
        }
        yield {
            globalBoundBox,
            timeInterval: averageTime,
            nodesTableLength: tableLength,
            version: +recordBatch.schema.metadata.get('version'),
            nodesUpdate: { batch: recordBatch, offset: batchOffset },
        };
        startTime = endTime;
        batchOffset += recordBatch.length;
    }
}

export function getViewport({ renderState: { camera } = {} } = {}) {
    let rect = { left: 0, right: 0, top: 0, bottom: 0 };
    if (camera) {
        const [left = 0, right = 0, top = 0, bottom = 0] = camera.getBounds();
        rect = {
            left: (left || 0),
            right: (right || 0),
            top: ((top * -1) || 0),
            bottom: ((bottom * -1) || 0),
        };
    }
    // if no camera (how??), reset the viewport to some reasonable default
    // so the server doesn't get confused and think we don't want to see anything
    if (!(Math.abs(rect.left + rect.right + rect.top + rect.bottom) > 0)) {
        rect.top = 1000;
        rect.left = -1000;
        rect.right = 1000;
        rect.bottom = -1000;
    }
    return rect;
}

function streamResponse(response, trail) {
    const { readable, writable } = new TransformStream();
    const reader = response.body.getReader();
    const writer = writable.getWriter();
    async function streamData() {
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            try {
                await writer.ready;
                await writer.write(value);
            } catch (error) {
                const errMsg = `Error while writing the streaming data traceparent=${trail}`;
                logger.error(error, errMsg);
                throw new Error(errMsg);
            }
        }
        try {
            await writer.ready;
            await writer.close();
        } catch (error) {
            logger.error(error, 'Error while closing the writer traceparent=%s', trail);
        }
    }
    streamData();
    const recordBatchStream = readable.pipeThrough(new TransformStream());
    const recordBatchResponse = new Response(recordBatchStream);
    return recordBatchResponse;
}

//TransformStream is unsupported in some older browsers (notably pre-2022
//Safari/Firefox): fall back to consuming the response directly.
if (window.TransformStream) {
    console.debug("Browser supports the TransformStream API!");
} else {
    console.debug("Browser doesn't support the TransformStream API; using the response object instead!");
    streamResponse = function(response, trail) {
        return response;
    };
}

const fetchRecordBatches = ((opts) =>
    async function* fetchBatches(uri, body, trail) {
        const init = { ...opts, body: qs.stringify({ ...body, filtered: true }) };
        if (trail) {
            init.headers.set('traceparent', trail);
        }

        //if (uri.startsWith('/streamgl/open')) {
        if (STUBBED && /\/streamgl\/(open|read|play)\b/.test(uri)) {
            const idMatch = /[?&]id=([^&]+)/.exec(uri);
            const baked = idMatch && resolveBakedName(decodeURIComponent(idMatch[1]));
            if (baked) { stubDataset = baked; }
            // pace like a GPU worker tick would; without this the simulating
            // read-loop (withArrowData repeat()) spins unthrottled on instant responses
            await new Promise((resolve) => setTimeout(resolve, 100));
            const buf = bakedOpen(stubDataset);
            const resp = new Response(buf);
            console.debug('stubbing /streamgl batch resp', {opts, uri, body, buf, resp, stubDataset, ...traceFields(trail)});
            yield* await RecordBatchReader.readAll(resp);
        } else if (STUBBED && uri.indexOf('/streamgl/stop') !== -1) {
            console.debug('stubbing stop', {opts, uri, body, init, ...traceFields(trail)});
            return;
        } else if (false && uri.startsWith('/streamgl/stop')) {
            console.debug('monitoring stop', {opts, uri, body, inits});
            yield* await RecordBatchReader.readAll(fetcher(uri, init));
        } else {
            console.debug('fetchRecordBatches fetchBatches', {opts, uri, init, ...traceFields(trail)});
            const response = await fetch(uri, init);
            if (response.status !== 200) {
                const errMsg = "Got an error fetching the data";
                logger.error({status: response.status, ...traceFields(trail)}, errMsg);
                yield new Error(errMsg);
            }
            const recordBatchResponse = streamResponse(response, trail);
            yield* await RecordBatchReader.readAll(recordBatchResponse);
        }
    }
)({
    method: `POST`,
    cache: `no-cache`,
    credentials: `same-origin`,
    headers: new Headers({
        // required to be able to post form-bodies via fetch
        'Content-Type':'application/x-www-form-urlencoded'
    })
});
